/**
 * Recibo de férias (P1) — a página do documento: configuração persistente
 * (editável em aberto/calculado), cálculo com resultado tipo holerite,
 * relatório completo (imprimir/baixar) e fechamento (imutável).
 */
import { useEffect, useState } from "react";
import { Button, Alert, Card, Divider, Group, NumberInput, Switch, Text, Textarea, Title } from "@mantine/core";
import { IconAlertTriangle, IconArrowLeft, IconCalculator, IconLock, IconPrinter, IconTrash } from "@tabler/icons-react";
import { api } from "../api";
import { navegar, useQueryRota, origemCompetencia, rotuloVoltarCompetencia } from "../roteador";
import { fmtCompetencia, fmtData, fmtDataHora, fmtMoeda } from "../format";
import { TabelaDados, Th } from "../componentes/listas";
import { BadgeEstadoProcessamento, VisualizadorRelatorio, type RelatorioAberto } from "../componentes/relatorio";
import { CompetenciaField, DataField, MoedaField } from "../componentes/entradas-mantine";
import { Confirmacao, ErroApiAlert, AvisosAlert, notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { ReabrirProcessamento } from "../componentes/reabrir-processamento";
import { TabelaEventosResultado, type EventoResultado } from "./FeriasPage";

interface ResultadoPessoa {
  codigoDominio: number;
  nome: string;
  eventos: EventoResultado[];
  totais: {
    proventosCentavos: number;
    descontosCentavos: number;
    liquidoCentavos: number;
    baseInssCentavos: number;
    baseFgtsCentavos: number;
    baseIrrfCentavos: number;
    fgtsCentavos: number;
  };
}

interface DetalheFerias {
  id: string;
  tipo: string;
  estado: string;
  competenciaPagamento: number;
  dataInicio: string | null;
  dias: number | null;
  mediasOverrideCentavos: number | null;
  /** M1.1/M1.3 — abono da solicitação (calculado no recibo desde o M1.3); 13º = M1.4. */
  diasAbono: number | null;
  antecipacao13: boolean | null;
  /** M1.1 — solicitação Convenia vinculada (null = recibo digitado manualmente). */
  solicitacao: {
    id: string;
    inicioGozo: string;
    fimGozo: string | null;
    diasGozo: number;
    diasAbono: number;
    antecipacao13: boolean;
    status: string;
    periodoAquisitivoId: string | null;
    coletivaTitulo: string | null;
  } | null;
  observacao: string | null;
  /** M1.12 — reflexo das coletivas: informativo de licença remunerada (split
   *  gozo × recesso) e diagnóstico do saldo negativo do período vinculado. */
  reflexoM112: {
    licencaRemunerada: { coletivaTitulo: string | null; diasDebitados: number; diasRecesso: number } | null;
    saldoDiagnostico: string | null;
  } | null;
  runId: string | null;
  funcionario: { id: string; nome: string; matriculaDominio: number | null } | null;
  mediasResolvidas: { codigoDominio: number; nome: string; fonte: string; valorInformadoCentavos: number | null; overrideCentavos: number | null; alerta?: string }[];
  avisosMedias: string[];
  runs: { id: string; motorVersion: string; executadoEm: string }[];
  ultimaRun: { id: string; motorVersion: string; executadoEm: string; resultado: { pessoas: ResultadoPessoa[] } } | null;
  artefatos: { id: string; tipo: string; nomeArquivo: string; criadoEm: string }[];
}

const ROTULO_FONTE_MEDIA: Record<string, string> = {
  informada: "informada neste recibo",
  historico: "automática (histórico de 12 meses)",
  zerada: "zerada (sem histórico)",
};

export function ReciboFeriasPage({ id }: { id: string }) {
  const [d, setD] = useState<DetalheFerias | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [fInicio, setFInicio] = useState<string | null>(null);
  const [fDias, setFDias] = useState<number | "">("");
  const [fAbono, setFAbono] = useState<number>(0); // M1.3 — abono editável ("o manual pode informar")
  const [fAnt13, setFAnt13] = useState(false); // M1.4 — adiantamento de 13º editável (flag Convenia ou manual)
  const [fMedia, setFMedia] = useState<number | null>(null);
  const [fObs, setFObs] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [relatorio, setRelatorio] = useState<RelatorioAberto | null>(null);
  const [confirmar, setConfirmar] = useState<{ titulo: string; consequencia: string; rotulo: string; acao: () => Promise<void> } | null>(null);
  // P2 — avisos do último cálculo (ex.: competência de pagamento fechada)
  const [avisosCalc, setAvisosCalc] = useState<string[]>([]);

  async function carregar() {
    const det = await api.get<DetalheFerias>(`/processamentos/${id}`);
    setD(det);
    setFInicio(det.dataInicio);
    setFDias(det.dias ?? "");
    setFAbono(det.diasAbono ?? 0);
    setFAnt13(det.antecipacao13 ?? false);
    setFMedia(det.mediasOverrideCentavos);
    setFObs(det.observacao ?? "");
  }

  useEffect(() => {
    carregar().catch(setErro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function acao(fn: () => Promise<unknown>, mensagem: string) {
    setOcupado(true);
    setErro(null);
    try {
      await fn();
      notificarOk(mensagem);
      await carregar();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    } finally {
      setOcupado(false);
      setConfirmar(null);
    }
  }

  /** Calcular/recalcular — a resposta traz `avisos` (P2: competência de
   *  pagamento fechada = calcula assim mesmo, com aviso amarelo persistente). */
  async function calcular() {
    await acao(async () => {
      const r = (await api.post(`/processamentos/${id}/calcular`)) as { avisos?: string[] };
      setAvisosCalc(r.avisos ?? []);
    }, d!.runs.length > 0 ? "Recibo recalculado." : "Recibo calculado.");
  }

  // 05/09/2026 (pedido do dono) — origem do deep-link preservada da FeriasPage:
  // "Voltar para a competência MM/AAAA" no topo (sem origem: nada muda)
  const origem = origemCompetencia(useQueryRota());

  if (!d && !erro) return <Text c="dimmed" size="sm">Carregando…</Text>;
  if (!d) return <ErroApiAlert erro={erro} />;

  const editavel = d.estado !== "fechado";
  const pessoa = d.ultimaRun?.resultado.pessoas[0] ?? null;
  const media = d.mediasResolvidas[0];
  // M1.4 — evento 9011 (adiantamento de 13º por ocasião das férias), quando calculado
  const evAdiant13 = pessoa?.eventos.find((e) => e.codigoDominio === 9011) ?? null;

  return (
    <>
      {origem && (
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={16} />}
          mb="xs"
          data-testid="voltar-competencia"
          onClick={() => navegar(origem)}
        >
          {rotuloVoltarCompetencia(origem)}
        </Button>
      )}
      <Group justify="space-between" mb="xs" wrap="wrap">
        <div>
          <Group gap="sm">
            <Title order={2}>Recibo de férias — {d.funcionario?.nome ?? "—"}</Title>
            <BadgeEstadoProcessamento estado={d.estado} />
          </Group>
          <Text size="sm" c="dimmed">
            Pagamento em {fmtCompetencia(d.competenciaPagamento)}
            {d.dataInicio && d.dias ? ` · gozo ${fmtData(d.dataInicio)} (${d.dias} dias)` : ""}
            {d.runs.length > 0 ? ` · ${d.runs.length} execução(ões)` : " · ainda não calculado"}
          </Text>
        </div>
        <Group>
          {d.estado === "aberto" && (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() =>
                setConfirmar({
                  titulo: "Excluir recibo de férias?",
                  consequencia: "O recibo ainda não foi calculado — excluir remove o documento sem deixar execuções.",
                  rotulo: "Excluir",
                  acao: async () => {
                    await api.del(`/processamentos/${id}`);
                    navegar("/ferias");
                  },
                })
              }
            >
              Excluir
            </Button>
          )}
          {d.estado !== "fechado" && (
            <Button
              leftSection={<IconCalculator size={16} />}
              loading={ocupado}
              onClick={() =>
                d.runs.length > 0
                  ? setConfirmar({
                      titulo: "Recalcular o recibo?",
                      consequencia:
                        "Uma NOVA execução é gravada (a anterior fica no histórico). O resultado exibido passa a ser o da nova execução.",
                      rotulo: "Recalcular",
                      acao: async () => {
                        const r = (await api.post(`/processamentos/${id}/calcular`)) as { avisos?: string[] };
                        setAvisosCalc(r.avisos ?? []);
                      },
                    })
                  : void calcular()
              }
            >
              {d.runs.length > 0 ? "Recalcular" : "Calcular"}
            </Button>
          )}
          {pessoa && (
            <Button
              variant="light"
              leftSection={<IconPrinter size={16} />}
              loading={ocupado}
              onClick={() =>
                void acao(async () => {
                  const r = (await api.post(`/processamentos/${id}/relatorio`)) as { id: string; nomeArquivo: string };
                  setRelatorio({ artefatoId: r.id, nomeArquivo: r.nomeArquivo, titulo: "Recibo de Férias" });
                }, "Relatório pronto.")
              }
            >
              Relatório completo
            </Button>
          )}
          {d.estado === "calculado" && (
            <Button
              color="teal"
              leftSection={<IconLock size={16} />}
              onClick={() =>
                setConfirmar({
                  titulo: "Fechar o recibo?",
                  consequencia: "Fechado é imutável: a configuração não pode mais ser editada e o recibo não recalcula.",
                  rotulo: "Fechar",
                  acao: () => api.post(`/processamentos/${id}/fechar`),
                })
              }
            >
              Fechar
            </Button>
          )}
          {d.estado === "fechado" && (
            <ReabrirProcessamento
              processamentoId={id}
              tipoRotulo="recibo de férias"
              onReaberto={(avisos) => {
                setAvisosCalc(avisos);
                void carregar();
              }}
            />
          )}
        </Group>
      </Group>
      <ErroApiAlert erro={erro} />
      <AvisosAlert avisos={avisosCalc} />

      <Card withBorder shadow="xs" mb="sm">
        <Title order={4} mb={4}>Configuração</Title>
        {(d.solicitacao || d.diasAbono || d.antecipacao13) && (
          <Alert color="teal" variant="light" mb="sm" role="status">
            {d.solicitacao && (
              <Text size="sm">
                Vinculado à solicitação {d.solicitacao.status === "accounting" ? "enviada à contabilidade" : "aprovada"} do
                Convenia{d.solicitacao.coletivaTitulo ? ` — férias coletivas: ${d.solicitacao.coletivaTitulo}` : ""} (gozo{" "}
                {fmtData(d.solicitacao.inicioGozo)}, {d.solicitacao.diasGozo} dias
                {d.solicitacao.diasAbono > 0 ? ` + ${d.solicitacao.diasAbono}d de abono` : ""}).
              </Text>
            )}
            {(d.diasAbono || d.antecipacao13) && (
              <Text size="xs" mt={d.solicitacao ? 4 : 0}>
                Previsto neste recibo:{" "}
                {[
                  d.diasAbono
                    ? `abono pecuniário de ${d.diasAbono} dias — calculado neste recibo (indenizatório: abono + 1/3, sem INSS/IRRF/FGTS)`
                    : null,
                  d.antecipacao13
                    ? "adiantamento da 1ª parcela do 13º — calculado neste recibo (50% da projeção, sem INSS/IRRF, com FGTS 8%; descontado na 2ª parcela de dezembro)"
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                .
              </Text>
            )}
            {d.reflexoM112?.licencaRemunerada && (
              <Text size="xs" mt={4}>
                Férias coletivas: este recibo calcula só o gozo debitado ({d.reflexoM112.licencaRemunerada.diasDebitados} dias de férias).
                Os {d.reflexoM112.licencaRemunerada.diasRecesso} dias de recesso são licença remunerada — dias normais de salário
                (sem pró-rata de férias e sem desconto de VT no mês).
              </Text>
            )}
          </Alert>
        )}
        {d.reflexoM112?.saldoDiagnostico && (
          <Alert color="orange" variant="light" mb="sm" role="alert" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">{d.reflexoM112.saldoDiagnostico}</Text>
          </Alert>
        )}
        {editavel ? (
          <>
            <Group grow mb="sm">
              <CompetenciaField label="Competência de pagamento" valor={d.competenciaPagamento} onChange={() => undefined} />
              <DataField label="Início do gozo" valor={fInicio} onChange={setFInicio} />
              <NumberInput label="Dias corridos" min={1} max={30} value={fDias} onChange={(v) => setFDias(v === "" ? "" : Number(v))} />
              <NumberInput
                label="Abono pecuniário (dias)"
                description="0 = sem abono · até 1/3 das férias (art. 143) · sem INSS/IRRF/FGTS"
                min={0}
                max={10}
                value={fAbono}
                onChange={(v) => setFAbono(v === "" ? 0 : Number(v))}
              />
              <MoedaField
                label="Média de variáveis (opcional)"
                description="Vazio = automática"
                valor={fMedia}
                onChange={setFMedia}
              />
            </Group>
            <Switch
              mb="sm"
              label="Adiantar a 1ª parcela do 13º junto com as férias"
              description="50% da projeção, sem INSS/IRRF, com FGTS 8% — descontado na 2ª parcela de dezembro. Requerimento legal: janeiro (Dec. 57.155/65 — o flag do Convenia é aceito como prova)"
              checked={fAnt13}
              onChange={(e) => setFAnt13(e.currentTarget.checked)}
            />
            <Textarea label="Observação" value={fObs} onChange={(e) => setFObs(e.currentTarget.value)} mb="sm" minRows={1} />
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {media
                  ? `Média resolvida: ${ROTULO_FONTE_MEDIA[media.fonte] ?? media.fonte}${media.valorInformadoCentavos !== null ? ` — ${fmtMoeda(media.valorInformadoCentavos)}/mês` : ""}${media.alerta ? ` (${media.alerta})` : ""}`
                  : ""}
              </Text>
              <Button
                size="xs"
                loading={ocupado}
                onClick={() =>
                  void acao(
                    () =>
                      api.patch(`/processamentos/${id}`, {
                        dataInicio: fInicio,
                        dias: fDias === "" ? null : fDias,
                        diasAbono: fAbono === 0 ? null : fAbono,
                        antecipacao13: fAnt13,
                        mediasOverrideCentavos: fMedia,
                        observacao: fObs.trim() || null,
                      }),
                    "Configuração salva.",
                  )
                }
              >
                Salvar configuração
              </Button>
            </Group>
            {d.estado === "calculado" && (
              <Text size="xs" c="orange" mt={4}>
                Configuração editada depois do cálculo? Recalcule para atualizar o resultado (o fechamento exige o
                cálculo em dia).
              </Text>
            )}
          </>
        ) : (
          <Text size="sm" c="dimmed">
            Recibo fechado — configuração imutável (gozo {d.dataInicio ? fmtData(d.dataInicio) : "—"}, {d.dias ?? "—"} dias
            {d.mediasOverrideCentavos !== null ? `, média informada ${fmtMoeda(d.mediasOverrideCentavos)}` : ""}
            {d.observacao ? ` · ${d.observacao}` : ""}).
          </Text>
        )}
      </Card>

      {pessoa && (
        <Card withBorder shadow="xs" mb="sm">
          <Title order={4} mb={4}>Resultado (última execução)</Title>
          <Group gap="xl" mb="sm" wrap="wrap">
            <div>
              <Text size="xs" c="dimmed">Proventos</Text>
              <Text fw={700}>{fmtMoeda(pessoa.totais.proventosCentavos)}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Descontos</Text>
              <Text fw={700}>{fmtMoeda(pessoa.totais.descontosCentavos)}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Líquido</Text>
              <Text fw={700} c="sillis.7" size="lg">{fmtMoeda(pessoa.totais.liquidoCentavos)}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">FGTS (8%)</Text>
              <Text fw={700}>{fmtMoeda(pessoa.totais.fgtsCentavos)}</Text>
            </div>
          </Group>
          <TabelaEventosResultado eventos={pessoa.eventos} tipo="P" />
          <TabelaEventosResultado eventos={pessoa.eventos} tipo="D" />
          {evAdiant13 && (
            <Alert color="indigo" variant="light" mt="sm" role="status">
              <Text size="sm">
                Adiantamento de 13º (1ª parcela por ocasião das férias): <strong>{fmtMoeda(evAdiant13.valorCentavos)}</strong>{" "}
                — 50% da projeção do ano, sem INSS/IRRF; o FGTS 8% sobre ele já está no total acima. O valor será
                descontado na 2ª parcela de dezembro (o sistema totaliza os adiantamentos do ano).
              </Text>
            </Alert>
          )}
          <Divider my="xs" />
          <Group gap="xl" wrap="wrap">
            <Text size="xs" c="dimmed">Base INSS: <strong>{fmtMoeda(pessoa.totais.baseInssCentavos)}</strong></Text>
            <Text size="xs" c="dimmed">Base FGTS: <strong>{fmtMoeda(pessoa.totais.baseFgtsCentavos)}</strong></Text>
            <Text size="xs" c="dimmed">Base IRRF: <strong>{fmtMoeda(pessoa.totais.baseIrrfCentavos)}</strong></Text>
          </Group>
        </Card>
      )}

      {d.runs.length > 0 && (
        <Card withBorder shadow="xs">
          <Title order={5} mb={4}>Histórico de execuções</Title>
          <TabelaDados>
            <thead>
              <tr>
                <Th>Data</Th>
                <Th>Motor</Th>
              </tr>
            </thead>
            <tbody>
              {d.runs.map((r) => (
                <tr key={r.id}>
                  <td>{fmtDataHora(r.executadoEm)}</td>
                  <td>
                    <Text size="xs" ff="monospace">{r.motorVersion}</Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </TabelaDados>
        </Card>
      )}

      <VisualizadorRelatorio relatorio={relatorio} onFechar={() => setRelatorio(null)} />
      <Confirmacao
        aberta={confirmar !== null}
        titulo={confirmar?.titulo ?? ""}
        consequencia={confirmar?.consequencia ?? ""}
        rotuloConfirmar={confirmar?.rotulo}
        onConfirmar={() => void acao(() => confirmar!.acao(), "Pronto.")}
        onCancelar={() => setConfirmar(null)}
      />
    </>
  );
}
